import { Avatar, Box, Button, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import theme from "../theme/theme";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchEmployees,
  fetchEmployeeById,
  createEmployee,
  updateEmployee as updateEmployeeApi,
  deleteEmployee,
  exportEmployees,
} from "../api/EmployeeService";
import Table from "../components/Table";
import { Edit, Delete, Visibility, Search, Download as DownloadIcon } from "@mui/icons-material";
import { InputAdornment, TextField } from "@mui/material";
import { ViewEmployeeDialog } from "../components/employee/VIewEmployeeDialog";
import { NewEmployeeDialog } from "../components/employee/NewEmployeeDialog";
import { EditEmployeeDialog } from "../components/employee/EditEmployeeDialog";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";

const DESIGNATION_OPTIONS = [
  { value: "", label: "All Positions" },
  { value: "sales", label: "Sales" },
  { value: "service", label: "Service" },
  { value: "engineer", label: "Engineer" },
  { value: "sub_admin", label: "Sub Admin" },
  { value: "others", label: "Others" },
];
const EmployeesPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [employee, setEmployee] = useState({});
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [addingEmployee, setAddingEmployee] = useState(false);
  const [errors, setErrors] = useState({});
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    employee_id: "",
    email: "",
    designation: "",
    phone: "",
    photo: "",
  });
  const [selectedEmployee, setSelectedEmployee] = useState({
    name: "",
    employee_id: "",
    email: "",
    designation: "",
    phone: "",
    photo: "",
    id: "",
  });
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [updatingEmployee, setUpdatingEmployee] = useState(false);

  // Delete confirm state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const response = await fetchEmployees(currentPage, debouncedSearch, designationFilter, perPage);
      setEmployees(response.data.data);
      setLinks(response.data.links);
      setMeta({ from: response.data.from, to: response.data.to, total: response.data.total });
    } catch (error) {
      toast.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };
  const viewEmployee = async (id) => {
    setViewLoading(true);
    setOpenViewDialog(true);
    try {
      const response = await fetchEmployeeById(id);
      setEmployee(response.data);
    } catch (error) {
      toast.error("Failed to fetch employee details");
    } finally {
      setViewLoading(false);
    }
  };
  const editEmployee = (id) => {
    setOpenEditDialog(true);
    fetchEmployeeForEdit(id);
  };
  const handleDeleteClick = (employeeId) => {
    setDeleteTarget(employeeId);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEmployee(deleteTarget);
      toast.success("Employee deleted successfully");
      setConfirmOpen(false);
      setDeleteTarget(null);
      fetchEmployeeData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete employee");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportEmployees(debouncedSearch, designationFilter);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "employees.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export employees");
    } finally {
      setExporting(false);
    }
  };

  const saveEmployee = async () => {
    setAddingEmployee(true);
    setErrors({});
    try {
      await createEmployee(newEmployee);
      toast.success("Employee created successfully");
      setAddingEmployee(false);
      setOpenNewDialog(false);
      setNewEmployee({
        name: "",
        employee_id: "",
        email: "",
        designation: "",
        phone: "",
        photo: "",
      });
      fetchEmployeeData();
    } catch (error) {
      if (error && error.data && error.data.errors) {
        setErrors(error.data.errors);
      } else {
        toast.error(error.message || "Failed to create employee");
      }
    } finally {
      setAddingEmployee(false);
    }
  };
  const fetchEmployeeForEdit = async (id) => {
    setFetchingEmployee(true);
    try {
      const response = await fetchEmployeeById(id);
      setSelectedEmployee(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch employee details for edit");
    } finally {
      setFetchingEmployee(false);
    }
  };
  const updateEmployee = async () => {
    setUpdatingEmployee(true);
    setErrors({});
    try {
      await updateEmployeeApi(selectedEmployee.id, selectedEmployee);
      toast.success("Employee updated successfully");
      setUpdatingEmployee(false);
      setOpenEditDialog(false);
      fetchEmployeeData();
    } catch (error) {
      if (error && error.data && error.data.errors) {
        setErrors(error.data.errors);
      } else {
        toast.error(error.message || "Failed to update employee");
      }
    } finally {
      setUpdatingEmployee(false);
    }
  };
  const actions = [
    {
      icon: <Visibility />,
      label: "View",
      onClick: (row) => {
        viewEmployee(row.id);
      },
      backgroundColor: theme.button.viewBackgroundColor,
    },
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => {
        editEmployee(row.id);
      },
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => {
        handleDeleteClick(row.id);
      },
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [designationFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    fetchEmployeeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, designationFilter, perPage]);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          All Employees
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            placeholder="Search employees..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220, backgroundColor: '#fff', borderRadius: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160, backgroundColor: '#fff', borderRadius: 1 }}>
            <InputLabel>Position</InputLabel>
            <Select
              value={designationFilter}
              label="Position"
              onChange={(e) => setDesignationFilter(e.target.value)}
            >
              {DESIGNATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ backgroundColor: "#fff" }}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LocationOnIcon />}
            sx={{ borderRadius: theme.button.borderRadius }}
            onClick={() => navigate("/live-tracking")}
          >
            Live Tracking
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: theme.button.borderRadius,
              backgroundColor: theme.palette.primary.main,
            }}
            onClick={() => setOpenNewDialog(true)}
          >
            Add Employee
          </Button>
        </Box>
      </Box>
      <Box>
        <Table
          columns={[
            {
              field: "photo",
              headerName: "Photo",
              renderCell: (row) => (
                <Avatar
                  src={row.photo || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    backgroundColor: theme.palette.primary.main,
                  }}
                >
                  {(row.name || "?").charAt(0).toUpperCase()}
                </Avatar>
              ),
            },
            { field: "employee_id", headerName: "Employee ID" },
            { field: "name", headerName: "Name" },
            { field: "email", headerName: "Email" },
            { field: "designation", headerName: "Position" },
            { field: "phone", headerName: "Phone" },
          ]}
          data={employees}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          actions={actions}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        ></Table>
      </Box>
      <ViewEmployeeDialog
        openViewDialog={openViewDialog}
        setOpenViewDialog={setOpenViewDialog}
        viewLoading={viewLoading}
        employee={employee}
        editEmployee={editEmployee}
      />
      <NewEmployeeDialog
        openNewDialog={openNewDialog}
        setOpenNewDialog={setOpenNewDialog}
        addingEmployee={addingEmployee}
        newEmployee={newEmployee}
        setNewEmployee={setNewEmployee}
        errors={errors}
        saveEmployee={saveEmployee}
      />
      <EditEmployeeDialog
        openEditDialog={openEditDialog}
        setOpenEditDialog={setOpenEditDialog}
        fetchingEmployee={fetchingEmployee}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        updateEmployee={updateEmployee}
        errors={errors}
        updatingEmployee={updatingEmployee}
        onPhotoUpdated={fetchEmployeeData}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete this employee? Their user account will also be removed. This action cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
};
export default EmployeesPage;
